// components/analytics/Analytics.tsx
import { useState, useEffect, useCallback, useRef } from "react";
import { Spin, Typography, Tag, Button, DatePicker, Select, Radio } from "antd";
import { useOutletContext } from "react-router-dom";
// Removed useNavigate import as it's not needed
import { useTheme } from "../../contexts/ThemeContext";
import {
  DownloadOutlined,
  CalendarOutlined,
  UserOutlined,
  FileTextOutlined,
  WalletOutlined,
  DollarOutlined,
  BarChartOutlined,
  FacebookOutlined,
  TeamOutlined,
  ClockCircleOutlined,
  RiseOutlined,
  GlobalOutlined,
  FilterOutlined,
  BankOutlined,
  AimOutlined,
  SoundOutlined,
  TrophyOutlined
} from "@ant-design/icons";
import { useAnalytics } from "../../contexts/AnalyticsContext";
import { useEffectiveAdminId } from "../../contexts/AdminSelectorContext";
import { UserAnalytics } from "./sections/UserAnalytics";
import { RequestAnalytics } from "./sections/RequestAnalytics";
import { WalletAnalytics } from "./sections/WalletAnalytics";
import { FinanceAnalytics } from "./sections/FinanceAnalytics";
import { GamificationAnalytics } from "./sections/GamificationAnalytics";
import { ExternalAnalytics } from "./sections/ExternalAnalytics";
import { ParseAnalytics } from "./sections/ParseAnalytics";
import { FacebookAnalytics } from "./sections/FacebookAnalytics";
import { CohortAnalytics } from "./sections/CohortAnalytics";
import { ChurnAnalytics } from "./sections/ChurnAnalytics";
import { TemporalPatterns } from "./sections/TemporalPatterns";
import { LiveStatsSection } from "./sections/LiveStatsSection";
import { UserTableAnalytics } from "../Broadcasting/components/UserTableAnalytics";

import ExportModal from "./components/ExportModal";
import type { AnalyticsFilters as FilterType } from "../../types/analytics";
import { API_BASE_URL } from "../../constants/config";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { getAndSanitize } from "../../shared/utils/sanitization";

// Configurar dayjs
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.tz.setDefault("America/Argentina/Buenos_Aires");

const { Text } = Typography;
const { RangePicker } = DatePicker;

type DateFilterOption = "all" | "today" | "yesterday" | "last7days" | "custom";
const DEFAULT_START_DATE = "2000-01-01T00:00:00.000Z";
const TIMEZONE = "America/Argentina/Buenos_Aires";

// Define context type from UserPanel
interface UserPanelOutletContext {
  activeAnalyticsTab: string;
  onAnalyticsTabChange: (tab: string) => void;
  filtersExpanded: boolean;
  onToggleFilters: () => void;
}

// Definir las secciones con sus íconos y colores
export const analyticsPages = [
  {
    key: "users",
    label: "Usuarios", 
    icon: <UserOutlined />,
    color: "#1890ff",
    description: "Análisis de usuarios y comportamiento"
  },
  {
    key: "broadcasting",
    label: "Broadcasting",
    icon: <SoundOutlined />,
    color: "#9254de",
    description: "Tabla de usuarios con filtros avanzados"
  },
  {
    key: "requests",
    label: "Solicitudes",
    icon: <FileTextOutlined />,
    color: "#52c41a", 
    description: "Cargas, retiros y procesamiento"
  },
  {
    key: "wallet",
    label: "Billetera",
    icon: <WalletOutlined />,
    color: "#fa8c16",
    description: "Transferencias y plataformas"
  },
  {
    key: "finance",
    label: "Finanzas",
    icon: <DollarOutlined />,
    color: "#722ed1",
    description: "Ingresos, gastos y balance"
  },
  {
    key: "gamificaciones",
    label: "Gamificaciones",
    icon: <TrophyOutlined />,
    color: "#eb2f96",
    description: "Bonus, rollover, referidos y puntos"
  },
  {
    key: "external",
    label: "External",
    icon: <GlobalOutlined />,
    color: "#13c2c2",
    description: "Análisis externos"
  },
  {
    key: "parse",
    label: "Parseo",
    icon: <BarChartOutlined />,
    color: "#eb2f96",
    description: "Análisis de parseo de datos"
  },
  {
    key: "facebook",
    label: "Facebook",
    icon: <FacebookOutlined />,
    color: "#1877f2",
    description: "Analytics de Facebook/TimbaVegas"
  },
  {
    key: "cohort",
    label: "Cohortes",
    icon: <TeamOutlined />,
    color: "#f5222d",
    description: "Análisis de cohortes de usuarios"
  },
  {
    key: "churn",
    label: "Churn",
    icon: <RiseOutlined />,
    color: "#faad14",
    description: "Análisis de abandono y riesgo"
  },
  {
    key: "temporal",
    label: "Temporal",
    icon: <ClockCircleOutlined />,
    color: "#531dab",
    description: "Patrones temporales y estacionalidad"
  },
  {
    key: "livestats",
    label: "En Vivo",
    icon: <RiseOutlined />,
    color: "#ff4d4f",
    description: "Estadísticas en tiempo real"
  }
];

export const Analytics: React.FC = () => {
  // Get context from UserPanel
  const context = useOutletContext<UserPanelOutletContext>();
  const activeTab = context?.activeAnalyticsTab || "users";
  const filtersExpanded = context?.filtersExpanded || false;
  const onToggleFilters = context?.onToggleFilters;

  const { analytics, isLoading, setFilters, filters } = useAnalytics();
  const { theme } = useTheme();
  const effectiveAdminId = useEffectiveAdminId(); // Get admin ID from context

  const [exportModalVisible, setExportModalVisible] = useState<boolean>(false);
  const [accountNames, setAccountNames] = useState<string[]>([]);
  const [selectedAccount, setSelectedAccount] = useState<string | undefined>();
  const [dateFilterOption, setDateFilterOption] = useState<DateFilterOption>("today");
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([
    dayjs().tz(TIMEZONE).startOf("day"),
    dayjs().tz(TIMEZONE).endOf("day"),
  ]);

  // Use ref to prevent infinite loops
  const hasInitialized = useRef(false);
  const prevAdminId = useRef<string | null>(effectiveAdminId);
  
  // Crear el objeto de período para mostrar
  const currentPeriod = {
    start: filters.dateRange.start,
    end: filters.dateRange.end
  };

  // Funciones para manejar los filtros de fecha
  const getDateRange = (option: DateFilterOption): [Dayjs, Dayjs] => {
    const now = dayjs().tz(TIMEZONE);
    switch (option) {
      case "today":
        return [now.startOf("day"), now.endOf("day")];
      case "yesterday": {
        const yesterday = now.subtract(1, "day");
        return [yesterday.startOf("day"), yesterday.endOf("day")];
      }
      case "last7days":
        return [now.subtract(6, "day").startOf("day"), now.endOf("day")];
      case "all":
        return [dayjs(DEFAULT_START_DATE).tz(TIMEZONE), now.endOf("day")];
      default:
        return [now.startOf("day"), now.endOf("day")];
    }
  };

  const emitFilters = useCallback(async (newDateRange: [Dayjs, Dayjs], accountName?: string) => {
    const token = getAndSanitize("token") || "";
    // null = SuperAdmin viewing all → use 'all'; specific ID = admin filtering
    const adminId = effectiveAdminId || 'all';

    const newFilters: FilterType = {
      dateRange: {
        start: newDateRange[0].toISOString(),
        end: newDateRange[1].toISOString(),
      },
      accountName: accountName || undefined,
      token,
      adminId,
    };
    await setFilters(newFilters);
  }, [effectiveAdminId, setFilters]);

  const handleDateFilterChange = async (value: DateFilterOption) => {
    setDateFilterOption(value);
    if (value !== "custom") {
      const newRange = getDateRange(value);
      setDateRange(newRange);
      await emitFilters(newRange, selectedAccount);
    }
  };

  const handleCustomDateChange = async (dates: [Dayjs, Dayjs] | null) => {
    if (dates) {
      setDateRange(dates);
      await emitFilters(dates, selectedAccount);
    }
  };

  const handleAccountChange = async (value: string | undefined) => {
    setSelectedAccount(value);
    await emitFilters(dateRange, value);
  };

  // Formatear período para mostrar
  const formatPeriodForDisplay = (period: { start: string; end: string }) => {
    if (!period.start || !period.end) return null;
    
    const startDate = dayjs(period.start).tz(TIMEZONE);
    const endDate = dayjs(period.end).tz(TIMEZONE);
    
    if (startDate.format("YYYY-MM-DD") === endDate.format("YYYY-MM-DD")) {
      return `${startDate.format("DD/MM/YYYY")} (${startDate.format("HH:mm")} - ${endDate.format("HH:mm")})`;
    }
    
    return `${startDate.format("DD/MM/YYYY HH:mm")} - ${endDate.format("DD/MM/YYYY HH:mm")}`;
  };

  const currentPeriodText = currentPeriod ? formatPeriodForDisplay(currentPeriod) : null;

  // Inicializar filtros con "today" al montar el componente
  useEffect(() => {
    const initializeFilters = async () => {
      const todayStart = dayjs().tz(TIMEZONE).startOf("day");
      const todayEnd = dayjs().tz(TIMEZONE).endOf("day");
      const todayRange: [Dayjs, Dayjs] = [todayStart, todayEnd];

      setDateRange(todayRange);

      // Call setFilters directly instead of emitFilters to avoid dependency issues
      const token = getAndSanitize("token") || "";
      // null = SuperAdmin viewing all → use 'all'; specific ID = admin filtering
      const adminId = effectiveAdminId || 'all';

      const newFilters: FilterType = {
        dateRange: {
          start: todayRange[0].toISOString(),
          end: todayRange[1].toISOString(),
        },
        accountName: undefined,
        token,
        adminId,
      };
      await setFilters(newFilters);

      hasInitialized.current = true;
      prevAdminId.current = effectiveAdminId;
    };

    // Only initialize once on mount or when effectiveAdminId changes
    if (!hasInitialized.current || prevAdminId.current !== effectiveAdminId) {
      initializeFilters();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveAdminId]); // setFilters is stable and doesn't need to be a dependency


  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        const token = getAndSanitize("token");
        if (!token) return;

        const response = await fetch(`${API_BASE_URL}/api/panel/users/accounts/list`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (response.ok) {
          const result = await response.json();
          if (result?.data && Array.isArray(result.data)) {
            const names = result.data
              .filter((item: any) => item?.accountName && typeof item.accountName === "string")
              .map((item: any) => item.accountName)
              .filter((name: string) => name.trim().length > 0);
            
            setAccountNames(Array.from(new Set(names as string[])).sort());
          }
        }
      } catch (error) {
        console.error("Failed to fetch account names:", error);
      }
    };

    fetchAccounts();
  }, []);

  const handleExportClick = () => {
    setExportModalVisible(true);
  };

  const renderActiveContent = () => {
    switch (activeTab) {
      case "users":
        return <UserAnalytics data={analytics.users} loading={isLoading} />;
      case "broadcasting":
        return (
          <div style={{ padding: '24px' }}>
            <UserTableAnalytics
              loading={isLoading}
              adminId={effectiveAdminId || getAndSanitize("userId") || ""}
              dateRange={filters.dateRange}
            />
          </div>
        );
      case "requests":
        return <RequestAnalytics data={analytics.requests} loading={isLoading} />;
      case "wallet":
        return <WalletAnalytics data={analytics.wallet} loading={isLoading} />;
      case "finance":
        return <FinanceAnalytics data={analytics.finance} loading={isLoading} />;
      case "gamificaciones":
        return <GamificationAnalytics data={analytics.gamification} loading={isLoading} />;
      case "external":
        return <ExternalAnalytics loading={isLoading} />;
      case "parse":
        return <ParseAnalytics loading={isLoading} />;
      case "facebook":
        return <FacebookAnalytics loading={isLoading} />;
      case "cohort":
        return <CohortAnalytics loading={isLoading} />;
      case "churn":
        return <ChurnAnalytics loading={isLoading} />;
      case "temporal":
        return <TemporalPatterns loading={isLoading} />;
      case "livestats":
        return <LiveStatsSection />;
      default:
        return <UserAnalytics data={analytics.users} loading={isLoading} />;
    }
  };

  return (
    <div className="analytics-container" style={{ position: 'relative', minHeight: '100vh', paddingLeft: 0 }}>

             {/* Panel Desplegable de Filtros */}
       {filtersExpanded && onToggleFilters && (
         <div
           style={{
             position: 'fixed',
             left: '240px', // Just after the sidebar
             top: 0,
             bottom: 0,
             zIndex: 999,
             width: 300,
             height: '100vh',
             background: theme === 'dark'
               ? '#0a0a0a'
               : '#ffffff',
             padding: '20px',
             boxShadow: theme === 'dark'
               ? '2px 0 8px rgba(0, 0, 0, 0.3)'
               : '2px 0 8px rgba(0, 0, 0, 0.08)',
             borderRight: theme === 'dark'
               ? '1px solid rgba(255, 255, 255, 0.1)'
               : '1px solid rgba(0, 0, 0, 0.06)',
             animation: filtersExpanded ? 'slideInRight 0.3s ease-out' : 'slideOutRight 0.3s ease-out',
             overflowY: 'auto',
             overflowX: 'hidden'
           }}
         >
          {/* Header */}
          <div style={{ 
            marginBottom: 20,
            borderBottom: theme === 'dark' 
              ? '1px solid rgba(255, 255, 255, 0.1)' 
              : '1px solid rgba(0, 0, 0, 0.06)',
            paddingBottom: 12
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <FilterOutlined style={{ color: '#722ed1', fontSize: 16 }} />
              <Text style={{ 
                fontSize: 14, 
                fontWeight: 600, 
                color: theme === 'dark' ? '#ffffff' : '#262626' 
              }}>
                Filtros de Analytics
              </Text>
            </div>
          </div>

          {/* Filtro de Período */}
          <div style={{ marginBottom: 16 }}>
            <Text style={{ 
              fontSize: 12, 
              fontWeight: 600, 
              color: theme === 'dark' ? '#d9d9d9' : '#595959',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              marginBottom: 8
            }}>
              <CalendarOutlined style={{ color: '#1890ff' }} />
              Período de Análisis
            </Text>
            <Radio.Group
              value={dateFilterOption}
              onChange={(e) => handleDateFilterChange(e.target.value)}
              style={{ width: '100%' }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <Radio.Button 
                  value="today"
                  style={{
                    borderRadius: 8,
                    border: 'none',
                    background: dateFilterOption === 'today' 
                      ? 'linear-gradient(135deg, #1890ff, #096dd9)' 
                      : 'rgba(24, 144, 255, 0.08)',
                    color: dateFilterOption === 'today' ? '#fff' : '#1890ff',
                    fontWeight: 500,
                    fontSize: 11,
                    height: 32
                  }}
                >
                  Hoy
                </Radio.Button>
                <Radio.Button 
                  value="yesterday"
                  style={{
                    borderRadius: 8,
                    border: 'none',
                    background: dateFilterOption === 'yesterday' 
                      ? 'linear-gradient(135deg, #52c41a, #389e0d)' 
                      : 'rgba(82, 196, 26, 0.08)',
                    color: dateFilterOption === 'yesterday' ? '#fff' : '#52c41a',
                    fontWeight: 500,
                    fontSize: 11,
                    height: 32
                  }}
                >
                  Ayer
                </Radio.Button>
                <Radio.Button 
                  value="last7days"
                  style={{
                    borderRadius: 8,
                    border: 'none',
                    background: dateFilterOption === 'last7days' 
                      ? 'linear-gradient(135deg, #fa8c16, #d46b08)' 
                      : 'rgba(250, 140, 22, 0.08)',
                    color: dateFilterOption === 'last7days' ? '#fff' : '#fa8c16',
                    fontWeight: 500,
                    fontSize: 11,
                    height: 32
                  }}
                >
                  Últimos 7 días
                </Radio.Button>
                <Radio.Button 
                  value="all"
                  style={{
                    borderRadius: 8,
                    border: 'none',
                    background: dateFilterOption === 'all' 
                      ? 'linear-gradient(135deg, #13c2c2, #08979c)' 
                      : 'rgba(19, 194, 194, 0.08)',
                    color: dateFilterOption === 'all' ? '#fff' : '#13c2c2',
                    fontWeight: 500,
                    fontSize: 11,
                    height: 32
                  }}
                >
                  Todo el tiempo
                </Radio.Button>
                <Radio.Button 
                  value="custom"
                  style={{
                    borderRadius: 8,
                    border: 'none',
                    background: dateFilterOption === 'custom' 
                      ? 'linear-gradient(135deg, #722ed1, #531dab)' 
                      : 'rgba(114, 46, 209, 0.08)',
                    color: dateFilterOption === 'custom' ? '#fff' : '#722ed1',
                    fontWeight: 500,
                    fontSize: 11,
                    height: 32
                  }}
                >
                  <AimOutlined style={{ marginRight: 4 }} />
                  Personalizado
                </Radio.Button>
              </div>
            </Radio.Group>
          </div>

          {/* Selector de fecha personalizada */}
          {dateFilterOption === "custom" && (
            <div style={{ marginBottom: 16 }}>
              <RangePicker
                value={dateRange}
                onChange={handleCustomDateChange}
                allowClear={false}
                style={{ 
                  width: '100%',
                  borderRadius: 8
                }}
                size="small"
                showTime={{
                  defaultValue: [
                    dayjs("00:00:00", "HH:mm:ss").tz(TIMEZONE),
                    dayjs("23:59:59", "HH:mm:ss").tz(TIMEZONE),
                  ],
                }}
                disabledDate={(current) =>
                  current && current > dayjs().tz(TIMEZONE).endOf("day")
                }
                placeholder={['Inicio', 'Fin']}
              />
            </div>
          )}

          {/* Filtro de Cuenta */}
          <div style={{ marginBottom: 16 }}>
            <Text style={{ 
              fontSize: 12, 
              fontWeight: 600, 
              color: theme === 'dark' ? '#d9d9d9' : '#595959',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              marginBottom: 8
            }}>
              <BankOutlined style={{ color: '#52c41a' }} />
              Cuenta de Análisis
            </Text>
            <Select
              value={selectedAccount}
              onChange={handleAccountChange}
              placeholder="Todas las cuentas"
              allowClear
              style={{ width: '100%' }}
              size="small"
              showSearch
              filterOption={(input, option: any) => {
                if (option && option.children && typeof option.children === 'string') {
                  return option.children.toLowerCase().includes(input.toLowerCase());
                }
                return false;
              }}
            >
              {accountNames.map((name) => (
                <Select.Option key={name} value={name}>
                  {name}
                </Select.Option>
              ))}
            </Select>
          </div>

          {/* Período Actual */}
          <div style={{ marginBottom: 16 }}>
            <Text style={{ 
              fontSize: 12, 
              fontWeight: 600, 
              color: theme === 'dark' ? '#d9d9d9' : '#595959',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              marginBottom: 8
            }}>
              <ClockCircleOutlined style={{ color: '#722ed1' }} />
              Período Actual
            </Text>
            {currentPeriodText ? (
              <div style={{ 
                background: theme === 'dark' 
                  ? 'rgba(24, 144, 255, 0.15)' 
                  : 'rgba(24, 144, 255, 0.08)',
                border: theme === 'dark'
                  ? '1px solid rgba(24, 144, 255, 0.3)'
                  : '1px solid rgba(24, 144, 255, 0.2)',
                borderRadius: 8,
                padding: '8px 12px',
                minHeight: 32,
                display: 'flex',
                alignItems: 'center',
                gap: 6
              }}>
                <CalendarOutlined style={{ color: '#1890ff', fontSize: 12 }} />
                <Text style={{ 
                  fontSize: 10, 
                  color: '#1890ff',
                  fontWeight: 500,
                  lineHeight: 1.2
                }}>
                  {currentPeriodText}
                </Text>
              </div>
            ) : (
              <div style={{ 
                background: theme === 'dark' 
                  ? 'rgba(255, 255, 255, 0.05)' 
                  : 'rgba(0, 0, 0, 0.02)',
                border: theme === 'dark'
                  ? '1px solid rgba(255, 255, 255, 0.1)'
                  : '1px solid rgba(0, 0, 0, 0.06)',
                borderRadius: 8,
                padding: '8px 12px',
                minHeight: 32,
                display: 'flex',
                alignItems: 'center'
              }}>
                <Text style={{ 
                  fontSize: 10, 
                  color: theme === 'dark' ? '#8c8c8c' : '#bfbfbf' 
                }}>
                  Sin período
                </Text>
              </div>
            )}
            {selectedAccount && (
              <Tag 
                color="green" 
                style={{ 
                  margin: '8px 0 0 0', 
                  fontSize: 10,
                  borderRadius: 6,
                  border: 'none'
                }}
              >
                {selectedAccount}
              </Tag>
            )}
          </div>

          {/* Botón exportar */}
          <Button 
            type="primary" 
            icon={<DownloadOutlined />}
            onClick={handleExportClick}
            size="small"
            block
            style={{ 
              borderRadius: 8,
              background: 'linear-gradient(135deg, #fa8c16, #d46b08)',
              border: 'none',
              boxShadow: '0 2px 8px rgba(250, 140, 22, 0.3)',
              fontWeight: 600,
              height: 32
            }}
          >
            Exportar
          </Button>
        </div>
      )}

      {/* Contenido principal */}
      <div style={{
        marginLeft: filtersExpanded ? '300px' : '0', // Only filters panel offset, sidebar handled by UserPanel
        paddingRight: 20,
        paddingTop: 16,
        paddingLeft: 20,
        transition: 'margin-left 0.3s ease-out'
      }}>
      <Spin spinning={isLoading}>
          <div style={{ padding: 24 }}>
            {renderActiveContent()}
          </div>
      </Spin>
      </div>

      <ExportModal
        visible={exportModalVisible}
        onCancel={() => setExportModalVisible(false)}
        accountNames={accountNames}
      />

      {/* Estilos para animaciones */}
      <style>
        {`
          @keyframes slideInRight {
            from {
              opacity: 0;
              transform: translateX(-20px);
            }
            to {
              opacity: 1;
              transform: translateX(0);
            }
          }
          
          @keyframes slideOutRight {
            from {
              opacity: 1;
              transform: translateX(0);
            }
            to {
              opacity: 0;
              transform: translateX(-20px);
            }
          }
        `}
      </style>
    </div>
  );
};

export default Analytics;
